import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Modal from 'react-modal';
import { Search } from 'lucide-react';
import { hideSearchPartnerPropertiesModal } from '@@/features/modal';
import { useSearchPartnerPropertiesQuery } from '@@/services/property';
import { RootState } from '@@/store';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import StandardInput from '@@/components/Inputs/StandardInput';

const schema = yup
  .object({
    propertyName: yup
      .string()
      .required('Veuillez entrer le nom de la résidence'),
  })
  .required();

const SearchPartnerPropertiesModal = () => {
  const dispatch = useDispatch();
  const showSearchPartnerPropertiesModal = useSelector(
    (state: RootState) => state.modal.showSearchPartnerPropertiesModal,
  );

  const {
    register,
    watch,
    reset,
    formState: { errors },
  } = useForm<{
    propertyName: string;
  }>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const closeModal = useCallback(() => {
    dispatch(hideSearchPartnerPropertiesModal());
    reset();
  }, [dispatch, reset]);

  const propertyName = watch('propertyName');

  const { data: properties } = useSearchPartnerPropertiesQuery(propertyName, {
    skip: propertyName?.length < 3,
  });

  useEffect(() => {
    return closeModal();
  }, [closeModal]);

  return (
    <Modal
      isOpen={showSearchPartnerPropertiesModal}
      onRequestClose={closeModal}
      className="absolute bg-white py-6 px-8 border border-neutral-300 rounded-modal shadow-modal top-20 left-8 sm:top-1/2 sm:left-1/2 sm:transform sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[600px]"
      contentLabel="Show User Profile Modal"
      ariaHideApp={false}
    >
      <div className="flex items-center justify-center gap-2 mb-6">
        <Search size={24} className="text-primary-600" />
        <h2 className="text-heading-md text-neutral-900">Rechercher dans votre parc</h2>
      </div>

      <hr className="mb-6 border-neutral-200" />

      <form>
        <StandardInput
          register={register}
          id="propertyName"
          label="Nom de la résidence *"
          type="text"
          placeholder="Nom de la résidence"
          error={errors.propertyName}
        />
      </form>

      {!!properties?.length && (
        <ul className="mt-6 space-y-3">
          {properties.map((property) => (
            <Link
              key={property.id}
              className="block border border-neutral-200 p-4 rounded-card hover:shadow-card-hover hover:border-primary-300 transition-all duration-200"
              to={`/properties/${property.id}`}
            >
              <p className="text-body font-semibold text-neutral-900 mb-1">{property.name}</p>
              <p className="text-body-sm text-neutral-600 mb-2">
                {property.units[0]?.address?.fullAddress}
              </p>
              <span className="inline-flex items-center px-3 py-1 rounded-badge text-body-xs font-medium bg-primary-100 text-primary-800">
                {property.zoneName.toUpperCase()}
              </span>
            </Link>
          ))}
        </ul>
      )}
    </Modal>
  );
};

export default SearchPartnerPropertiesModal;
